import { MappingContextProvider } from '@/contexts/MappingContext'
import { CrossroadsEntityGrid } from '@pages/Dashboard/panels/ReferenceDataPanel/components/CrossroadsEntityGrid'
import { CrossroadsMappingGrid } from '@pages/Dashboard/panels/ReferenceDataPanel/components/CrossroadsMappingGrid'
import { useEffect, useMemo, useRef } from 'react'
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels'
import { useSearchParams } from 'react-router-dom'
import { useEntityCache } from '../../../../contexts/EntityCacheContext'
import { useObject } from '../../../../contexts/ObjectContext'
import '../../styles/ReferenceDataPanel.css'

/**
 * Main ReferenceDataPanel coordinator component
 * Orchestrates all the extracted components and hooks for reference data management
 */
export function ReferenceDataPanel() {
  const { selectedObject, setSelectedObject, availableObjects } = useObject()
  const { ensureEntitiesLoaded } = useEntityCache()

  // A deep link (e.g. from the audit modal) can request a mapping type via
  // ?object=; apply it once on load so the go_ids/source_ids filters land on
  // the right grid.
  const [searchParams] = useSearchParams()
  const appliedObjectParam = useRef(false)
  useEffect(() => {
    if (appliedObjectParam.current) return
    const requested = searchParams.get('object')
    if (requested && availableObjects.some((o) => o.value === requested)) {
      setSelectedObject(requested)
      appliedObjectParam.current = true
    }
  }, [searchParams, availableObjects, setSelectedObject])

  const mappingType = useMemo(() => {
    return selectedObject ? selectedObject.mapping_type : null
  }, [selectedObject])

  const entityType = useMemo(() => {
    return selectedObject ? selectedObject.entity_type : null
  }, [selectedObject])

  // Pre-load entity cache when panel mounts or entity type changes
  useEffect(() => {
    if (mappingType) {
      ensureEntitiesLoaded(mappingType)
    }
  }, [mappingType, ensureEntitiesLoaded])

  return (
    <MappingContextProvider>
      <PanelGroup direction='horizontal'>
        <Panel defaultSize={60} minSize={25}>
          <CrossroadsMappingGrid key={mappingType} mappingType={mappingType} />
        </Panel>
        <PanelResizeHandle
          style={{
            width: '8px',
            background: 'var(--gray-200)',
            cursor: 'col-resize',
          }}
        />
        <Panel defaultSize={40} minSize={25}>
          <CrossroadsEntityGrid key={entityType} filterType={entityType} />
        </Panel>
      </PanelGroup>
    </MappingContextProvider>
  )
}
